import { getFallbackFPS } from '@goldcast/player-config/src/utils/fpsUtils';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { DEFAULT_ZOOM_LEVEL } from '@goldcast/player-config/src/constants/clip-defaults';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import createStore from '@/stores/store';

const MIN_ZOOM_MULTIPLIER = 0.6;
export const MAX_ZOOM_VALUE = 10; // 2px/frame
const ZOOM_STEPS = 10;

export function calculateZoomMultipliers(baseZoomLevel: number): number[] {
  if (baseZoomLevel === 0) {
    return Array(10).fill(1);
  }

  const maxMultiplier = MAX_ZOOM_VALUE / baseZoomLevel;

  // Start with fixed values 0.6 and 1
  const multipliers = [0.6, 1];

  // Calculate remaining multipliers exponentially from 1 to max
  const remainingSteps = ZOOM_STEPS - 1; // Subtract 2 for the fixed values
  const ratio = Math.pow(maxMultiplier, 1 / (remainingSteps - 1));

  for (let i = 1; i < remainingSteps; i++) {
    multipliers.push(Math.pow(ratio, i));
  }

  return multipliers;
}

// Define the minimum and maximum zoom levels
// Zoom level 1 will mean 1px = 1frame and 24 will mean 24px = 1frame
export const MIN_ZOOM_LEVEL = 120 / (60 * 5 * getFallbackFPS());
export const MAX_ZOOM_LEVEL = 100;
export const ZOOM_STEP = 1;

// Create a store for the zoom level
export const zoomLevelStore = createStore<{
  baseZoomLevel: number;
  zoomLevel: number;
  fps: number;
}>({
  baseZoomLevel: MIN_ZOOM_LEVEL,
  zoomLevel: DEFAULT_ZOOM_LEVEL,
  fps: getFallbackFPS()
});

/**
 * Set the zoom level to a specific value
 * @param value - The new zoom level value
 */
export function setZoomValue(value: number) {
  const baseZoomLevel = zoomLevelStore.getSnapshot().baseZoomLevel;
  const zoomMultipliers = calculateZoomMultipliers(baseZoomLevel);

  const minPossibleZoom = zoomMultipliers[0] * baseZoomLevel;
  const maxPossibleZoom = zoomMultipliers[zoomMultipliers.length - 1] * baseZoomLevel;

  // Clamp the value to valid range
  const clampedValue = Math.max(minPossibleZoom, Math.min(maxPossibleZoom, value));
  zoomLevelStore.set(state => ({ ...state, zoomLevel: clampedValue }));
}

export function increaseZoomLevel() {
  const baseZoomLevel = zoomLevelStore.getSnapshot().baseZoomLevel;
  const currentZoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
  // Calculate dynamic zoom multipliers based on current base zoom level
  const zoomMultipliers = calculateZoomMultipliers(baseZoomLevel);

  // Find the next higher zoom level
  const nextMultiplier = zoomMultipliers.find(
    multiplier => roundToNDecimalPlaces(multiplier * baseZoomLevel, 2) > roundToNDecimalPlaces(currentZoomLevel, 2)
  );

  if (nextMultiplier) {
    setZoomValue(nextMultiplier * baseZoomLevel);
  } else {
    setZoomValue(MAX_ZOOM_VALUE);
  }
  setTimeout(() => {
    EventBus.dispatch(CustomEvents.ZoomValueChangedFromRangeSlider);
  }, 0);
}

export function decreaseZoomLevel() {
  const baseZoomLevel = zoomLevelStore.getSnapshot().baseZoomLevel;
  const currentZoomLevel = zoomLevelStore.getSnapshot().zoomLevel;

  const zoomMultipliers = calculateZoomMultipliers(baseZoomLevel);

  const currentIndex = zoomMultipliers.findIndex(multiplier => multiplier * baseZoomLevel >= currentZoomLevel);

  const previousIndex = Math.max(0, currentIndex - 1);
  const previousMultiplier = zoomMultipliers[previousIndex];

  if (previousMultiplier) {
    setZoomValue(previousMultiplier * baseZoomLevel);
  } else {
    setZoomValue(baseZoomLevel * MIN_ZOOM_MULTIPLIER);
  }
  setTimeout(() => {
    EventBus.dispatch(CustomEvents.ZoomValueChangedFromRangeSlider);
  }, 0);
}

export function setBaseZoomLevel(value: number) {
  zoomLevelStore.set(state => ({ ...state, baseZoomLevel: value }));
}

export function setRecordingFps(value: number) {
  zoomLevelStore.set(state => ({ ...state, fps: value }));
}
